import {Badge, Box, Highlight, HStack, Icon, Separator, Stack, Text, useDisclosure} from "@chakra-ui/react";
import {Button} from "../components/ui/button";
import ContentWrapper from "./components/ContentWrapper";
import {ArrowDownTrayIcon, LockClosedIcon} from "@heroicons/react/20/solid";
import {DocumentExternal} from "platformed-client/types";
import externalApi from "../api/external";
import {usePromiseState} from "../hooks/promiseState";
import {useTime} from "../hooks/time";
import {relativeDateView} from "../utils/time";
import {RequestDocumentAccessDialog} from "./components/RequestDocumentAccessDialog";
import {useClientAccount} from "./hooks/clientAccount";
import Search from "../components/Search";
import {useSearchParam} from "../hooks/searchParams";
import {useTrustCenterDocuments} from "./config";
import T from "platformed-components/i18n/T";
import useTranslate from "platformed-components/i18n/useTranslate";

export function DocumentDownloadButton({document}: {document: DocumentExternal}) {
  const clientAccount = useClientAccount();
  const dialog = useDisclosure();
  const [downloading, download] = usePromiseState(async () => {
    await externalApi.trustCenters.downloadDocument(clientAccount, document.document_id);
  }, [clientAccount, document.document_id]);

  return !document.is_readable ? (
    <>
      <Button size="xs" onClick={dialog.onOpen} variant="secondary">
        <Icon as={LockClosedIcon} />
        <T keyName="c.Request_access" />
      </Button>
      <RequestDocumentAccessDialog
        open={dialog.open}
        onClose={dialog.onClose}
        initialSelectedDocumentIds={[document.document_id]}
      />
    </>
  ) : (
    <Button
      size="xs"
      onClick={download}
      loading={downloading.inProgress}
      disabled={downloading.inProgress}
      variant="primary"
    >
      <Icon as={ArrowDownTrayIcon} />
      <T keyName="c.Download" />
    </Button>
  );
}

export const Document = ({document, query}: {document: DocumentExternal; query?: string}) => {
  const lastUpdateText = useTime(relativeDateView(document.last_update), [document.last_update]);
  return (
    <Box px={4} py={4}>
      <HStack>
        <Stack flex={2} align="flex-start">
          <Text fontSize="md" fontWeight="semibold" color="gray.700">
            {query !== undefined ? (
              <Highlight query={query} styles={{bg: "yellow.200"}}>
                {document.name}
              </Highlight>
            ) : (
              document.name
            )}
          </Text>
          <Badge>
            <T keyName="c.updated[with_time]" params={{time: lastUpdateText}} />
          </Badge>
        </Stack>
        <DocumentDownloadButton document={document} />
      </HStack>
    </Box>
  );
};

const Documents = () => {
  const t = useTranslate();
  const [searchQuery, setSearchQuery] = useSearchParam("q");

  const documents = useTrustCenterDocuments().filter(d =>
    searchQuery ? d.name.toLowerCase().includes(searchQuery.toLowerCase()) : true,
  );

  const notFoundText = searchQuery ? t("trust_center.documents.no_results") : t("trust_center.documents.empty");

  return (
    <ContentWrapper py={12}>
      <Stack gap={4}>
        <Search query={searchQuery ?? ""} onChange={setSearchQuery} placeholder={t("c.Search_documents[ellipsis]")} />
        {documents.length !== 0 ? (
          <Stack border="1px solid" borderColor="gray.200" rounded="md" separator={<Separator />} gap={0}>
            {documents.map((document: DocumentExternal) => (
              <Document
                key={document.document_id}
                document={document}
                query={searchQuery !== null ? searchQuery : undefined}
              />
            ))}
          </Stack>
        ) : (
          <Box border="1px solid" borderColor="gray.200" rounded="md" p={4}>
            <Text fontSize="md" color="gray.500">
              {notFoundText}
            </Text>
          </Box>
        )}
      </Stack>
    </ContentWrapper>
  );
};

export default Documents;
